import React from "react";
import { ConfirmDialog } from "./ConfirmDialog";

interface DeleteServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceName: string;
  /**
   * Performs the delete. May be async — errors surface inline in the dialog
   * and the dialog stays open so the user can retry. The dialog closes itself
   * on success.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * DeleteServiceDialog
 *
 * Confirmation dialog for archiving (soft-deleting) a service. Thin wrapper
 * around the shared {@link ConfirmDialog}, which handles pending/lock/error
 * state and the AlertDialog semantics.
 */
export const DeleteServiceDialog: React.FC<DeleteServiceDialogProps> = ({
  open,
  onOpenChange,
  serviceName,
  onConfirm,
}) => {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      destructive
      title="Archive Service"
      confirmLabel="Archive Service"
      pendingLabel="Archiving…"
      onConfirm={onConfirm}
      description={
        <>
          <p>
            Are you sure you want to archive{" "}
            <strong className="text-foreground">{serviceName}</strong>?
          </p>
          <div className="rounded-md bg-muted p-3 text-sm">
            <p className="mb-2 font-medium text-foreground">
              This action will:
            </p>
            <ul className="list-inside list-disc space-y-1 text-muted-foreground">
              <li>Archive the service (soft delete)</li>
              <li>Hide it from your active services list</li>
              <li>Preserve deployment history and data</li>
              <li>Stop showing related deployments</li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            The service will be archived but historical data will be preserved
            for record-keeping.
          </p>
        </>
      }
    />
  );
};

export default DeleteServiceDialog;
